import { Checkbox, Switch } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { mapTransportDistance } from '@/app/components/hotel-detail/common/transportDistance';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  type AirportFilterType,
  type AirportsClearAllFilterChange,
  type AirportsFilterChange,
  BoundHeader,
  Filter,
  FilterContainer,
  FilterHeader,
  FilterPrice,
  FilterTitle,
  type SameAirportInboundFilterChange,
  type SameAirportOutboundFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import useTransportsFetch from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { FilterLoadingLayer } from './ChangeTransportFiltersLoading';
import type { ClickParams } from '../../search/Curiosity/Analytics/utils';
import { useTransportPageFilterTracking } from './hooks/useTransportPageFilterTracking';

const AirportFilterHeader = styled.div`
  font-weight: ${(p) => p.theme.kallisto.font.weight.bold};
  padding: 0.75rem 0;
`;

export const mapFilterTypeEventOrigin: { [key in AirportFilterType]: AirportsFilterEventOrigin } = {
  outboundDepartureAirports: 'DEPARTURE_AIRPORTS_OUTBOUND_FILTER',
  outboundArrivalAirports: 'ARRIVAL_AIRPORTS_OUTBOUND_FILTER',
  inboundArrivalAirports: 'ARRIVAL_AIRPORTS_RETURN_FILTER',
  inboundDepartureAirports: 'DEPARTURE_AIRPORTS_RETURN_FILTER',
};

function getAirportFilter(type: AirportFilterType, newFilter: AirportFilter[]): AirportsFilterChange {
  return {
    newState: { [type]: newFilter } as AirportsFilterChange['newState'],
    event: { type: 'FILTER', origin: mapFilterTypeEventOrigin[type] },
  };
}

function getAirportFilterChangeData(
  type: AirportFilterType,
  filter: AirportFilter[],
  newFilterValue: string,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): AirportsFilterChange {
  const newFilter: AirportFilter[] = filter.map((val: AirportFilter) =>
    val.iata === newFilterValue ? { ...val, selected: !val.selected } : val
  );
  sendStratrackFlightPageFilterEvent({
    filterName: mapFilterTypeEventOrigin[type],
    value: newFilterValue,
    deselectFlag: filter.find((f) => f.iata === newFilterValue)?.selected || false,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_${mapFilterTypeEventOrigin[type]}_airport_filters`,
    event_context: 'change_transport_page',
    event_value: newFilterValue,
  });
  return getAirportFilter(type, newFilter);
}

export function getFilterInboundSameAirport(
  enabled: boolean,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): SameAirportInboundFilterChange {
  sendStratrackFlightPageFilterEvent({
    filterName: 'INBOUND_SAME_AIRPORT_FILTER',
    value: enabled.toString(),
    deselectFlag: !enabled,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_inbound_same_airport_filters`,
    event_context: 'change_transport_page',
    event_value: `enabled_${enabled}`,
  });
  return {
    newState: { inboundSameAirport: { on: enabled } },
    event: { type: 'FILTER', origin: 'INBOUND_SAME_AIRPORT_FILTER' },
  };
}

export function getFilterOutboundSameAirport(
  enabled: boolean,
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void,
  sendStratrackFlightPageFilterEvent: (payload: StartrackFilterPayload) => void
): SameAirportOutboundFilterChange {
  sendStratrackFlightPageFilterEvent({
    filterName: 'OUTBOUND_SAME_AIRPORT_FILTER',
    value: enabled.toString(),
    deselectFlag: !enabled,
  });
  sendCuriosityClickEvent({
    event_name: `click_on_outbound_same_airport_filters`,
    event_context: 'change_transport_page',
    event_value: `enabled_${enabled}`,
  });
  return {
    newState: { outboundSameAirport: { on: enabled } },
    event: { type: 'FILTER', origin: 'OUTBOUND_SAME_AIRPORT_FILTER' },
  };
}

type ChangeTransportAirportsFilterProps = {
  inboundArrivalAirports?: AirportFilter[];
  inboundDepartureAirports?: AirportFilter[];
  inboundSameAirport?: SameAirportFilter;
  outboundArrivalAirports?: AirportFilter[];
  outboundDepartureAirports?: AirportFilter[];
  outboundSameAirport?: SameAirportFilter;
  onChange: (
    filterChange:
      | AirportsFilterChange
      | SameAirportInboundFilterChange
      | SameAirportOutboundFilterChange
      | AirportsClearAllFilterChange
  ) => void;
};

export default function ChangeTransportAirportsFilter(props: ChangeTransportAirportsFilterProps) {
  const {
    inboundArrivalAirports,
    inboundDepartureAirports,
    inboundSameAirport,
    outboundArrivalAirports,
    outboundDepartureAirports,
    outboundSameAirport,
    onChange,
  } = props;

  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  const { data: alternativeTransportsData } = useTransportsFetch();
  const transportDistance = (alternativeTransportsData as FullAvailableTransportResponse)?.distances;
  const distancesData = mapTransportDistance(transportDistance);

  const shouldShowInboundSameArrivalAirport = inboundDepartureAirports && inboundDepartureAirports.length > 1;

  const shouldShowOutboundSameDepartureAirport = outboundDepartureAirports && outboundDepartureAirports.length > 1;

  const shouldShowOutbound = outboundDepartureAirports || outboundArrivalAirports;
  const shouldShowInbound = inboundDepartureAirports || inboundArrivalAirports;

  return shouldShowInboundSameArrivalAirport ||
    shouldShowOutboundSameDepartureAirport ||
    shouldShowOutbound ||
    shouldShowInbound ? (
    <Filter>
      <FilterHeader>
        <FilterTitle>
          {isTrain
            ? t('vacation.trainavailability.filters.stations')
            : t('vacation.flightavailability.filters.airports')}
        </FilterTitle>
      </FilterHeader>
      {shouldShowOutboundSameDepartureAirport && (
        <FilterContainer
          css={`
            flex-direction: row;
            justify-content: space-between;
            column-gap: 0.5rem;
          `}
        >
          <span>
            {isTrain
              ? t('vacation.trainavailability.filters.leavearrivesamestation')
              : t('vacation.flightavailability.filters.leavearrivesameairport')}
          </span>
          <Switch
            label=""
            isLabelVisible={false}
            checked={outboundSameAirport?.on}
            onChange={(enabled) =>
              onChange(
                getFilterOutboundSameAirport(enabled, sendCuriosityClickEvent, sendStratrackFlightPageFilterEvent)
              )
            }
          />
        </FilterContainer>
      )}
      {shouldShowInboundSameArrivalAirport && (
        <FilterContainer
          css={`
            flex-direction: row;
            justify-content: space-between;
            column-gap: 0.5rem;
          `}
        >
          <span>
            {isTrain
              ? t('vacation.trainavailability.filters.arriveleavesamestation')
              : t('vacation.flightavailability.filters.arriveleavesameairport')}
          </span>
          <Switch
            label=""
            isLabelVisible={false}
            checked={inboundSameAirport?.on}
            onChange={(enabled) =>
              onChange(
                getFilterInboundSameAirport(enabled, sendCuriosityClickEvent, sendStratrackFlightPageFilterEvent)
              )
            }
          />
        </FilterContainer>
      )}
      {shouldShowOutbound ? (
        <FilterContainer>
          <BoundHeader type="outbound" />
          {outboundDepartureAirports && (
            <AirportFiltersGroup
              title={
                isTrain
                  ? t('vacation.common.filters.station.departure.title')
                  : t('vacation.common.filters.airport.departure.title')
              }
              filters={outboundDepartureAirports}
              distances={[]}
              onChange={onChange}
              type="outboundDepartureAirports"
              origin="DEPARTURE_AIRPORTS_OUTBOUND_FILTER"
            />
          )}
          {outboundArrivalAirports && (
            <AirportFiltersGroup
              title={
                isTrain
                  ? t('vacation.common.filters.station.arrival.title')
                  : t('vacation.common.filters.airport.arrival.title')
              }
              filters={outboundArrivalAirports}
              distances={distancesData}
              onChange={onChange}
              type="outboundArrivalAirports"
              origin="ARRIVAL_AIRPORTS_OUTBOUND_FILTER"
            />
          )}
        </FilterContainer>
      ) : null}
      {shouldShowInbound ? (
        <FilterContainer>
          <BoundHeader type="inbound" />
          {inboundDepartureAirports && (
            <AirportFiltersGroup
              title={
                isTrain
                  ? t('vacation.common.filters.station.departure.title')
                  : t('vacation.common.filters.airport.departure.title')
              }
              filters={inboundDepartureAirports}
              distances={distancesData}
              onChange={onChange}
              type="inboundDepartureAirports"
              origin="DEPARTURE_AIRPORTS_RETURN_FILTER"
            />
          )}
          {inboundArrivalAirports && (
            <AirportFiltersGroup
              title={
                isTrain
                  ? t('vacation.common.filters.station.arrival.title')
                  : t('vacation.common.filters.airport.arrival.title')
              }
              filters={inboundArrivalAirports}
              distances={[]}
              onChange={onChange}
              type="inboundArrivalAirports"
              origin="ARRIVAL_AIRPORTS_RETURN_FILTER"
            />
          )}
        </FilterContainer>
      ) : null}
    </Filter>
  ) : null;
}

type AirportFiltersGroupProps = {
  filters: AirportFilter[];
  distances: TransportDistances;
  type: AirportFilterType;
  title: string;
  origin: AirportsFilterEventOrigin;
  onChange: (filterChange: AirportsFilterChange) => void;
};

function AirportFiltersGroup(props: AirportFiltersGroupProps) {
  const { filters, type, title, distances, origin, onChange } = props;
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  return (
    <div
      css={css`
        &:not(:last-child) {
          border-bottom: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
          padding-bottom: 1rem;
        }
      `}
    >
      <AirportFilterHeader>{title}</AirportFilterHeader>
      <div
        css={`
          display: flex;
          flex-direction: column;
          row-gap: 1rem;
          position: relative;
        `}
      >
        {filters.map((filter) => (
          <AirportFilterCheck
            key={filter.iata}
            {...filter}
            distances={distances}
            onAirportFilterChange={(value) =>
              onChange(
                getAirportFilterChangeData(
                  type,
                  filters,
                  value,
                  sendCuriosityClickEvent,
                  sendStratrackFlightPageFilterEvent
                )
              )
            }
          />
        ))}
        <FilterLoadingLayer origin={origin} />
      </div>
    </div>
  );
}

type AirportFilterCheckProps = {
  distances: TransportDistances;
  onAirportFilterChange: (iata: string) => void;
} & AirportFilter;

function AirportFilterCheck(props: AirportFilterCheckProps) {
  const { enabled, distances, selected, minPrice, iata, name, onAirportFilterChange } = props;
  const { t } = useTranslation();
  const distance = distances.find((data) => data.code === iata)?.distanceWithUnit;
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}
    >
      <div
        css={`
          display: flex;
          flex-direction: column;
        `}
      >
        {distance && (
          <span
            css={css`
              font-size: ${(p) => p.theme.kallisto.font.size['150']};
              color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
            `}
          >{`${distance.value} ${distance.unit} ${t('vacation.flightavailability.filter.airportdistance')}`}</span>
        )}
        <span>{`${name} (${iata})`}</span>
        <FilterPrice price={enabled ? minPrice : 0} />
      </div>
      <Checkbox
        checkbox={{ id: iata }}
        disabled={!enabled}
        isChecked={selected}
        onChange={() => onAirportFilterChange(iata)}
        // @ts-ignore
        labelPosition={'start'} // TODO change design system, this type could be defined as strings ('start' | 'end') instead of enum
        // padding={{ left: 0.5, right: 0, top: 1, bottom: 1 }}
      />
    </div>
  );
}
